'use client';

import { InputMask } from '@react-input/mask';
import cx from 'classnames';
import type React from 'react';
import { forwardRef, useState } from 'react';
import IconButtonForPass from '@/shared/Buttons/IconButton/iconButtonForPass';
import { Icons } from '@/shared/Icons/Icons';
import styles from './controlledInput.module.css';

interface ControlledInputProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  type?: string;
  minWidth?: number;
  placeholder?: string;
  required?: boolean;
  autocomplete?: string;
  mask?: string;
  min?: number;
  max?: number;
  error?: string;
  className?: string;
}

const ControlledInput = forwardRef<HTMLInputElement, ControlledInputProps>(
  (
    {
      label,
      value,
      onChange,
      onBlur,
      type = 'text',
      minWidth = 100,
      placeholder = '',
      required = false,
      autocomplete = '',
      mask,
      min,
      max,
      error,
      className,
    },
    ref,
  ) => {
    const [showPassword, setShowPassword] = useState(type !== 'password');

    const toggleShowPassword = () => {
      setShowPassword((prev) => !prev);
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(event.target.value);
    };

    // Function to add asterisk to placeholder for required fields
    const getPlaceholderWithAsterisk = () => {
      return required ? `${placeholder}*` : placeholder;
    };

    const isInvalid = !!error;

    if (mask) {
      return (
        <div>
          {label ? (
            <label className={styles.label}>
              {label}:{required && <span className={styles.required}>*</span>}
            </label>
          ) : null}
          <div className={styles.passwordContainer}>
            <InputMask
              mask={mask}
              replacement={{ _: /\d/ }}
              value={value}
              onChange={handleChange}
              onBlur={onBlur}
              ref={ref}
              style={{ minWidth }}
              placeholder={getPlaceholderWithAsterisk()}
              className={cx(
                styles.mainInput,
                isInvalid ? styles.error : '',
                className,
              )}
              autoComplete={autocomplete}
              type={type}
              {...(type === 'number' && min !== undefined ? { min } : {})}
              {...(type === 'number' && max !== undefined ? { max } : {})}
            />
          </div>
          <div className={styles.errorContainer}>
            {isInvalid && <span className={styles.errorText}>{error}</span>}
          </div>
        </div>
      );
    }

    return (
      <div>
        {label ? (
          <label className={styles.label}>
            {label}:{required && <span className={styles.required}>*</span>}
          </label>
        ) : null}
        <div className={styles.passwordContainer}>
          <input
            type={
              type === 'password' ? (showPassword ? 'text' : 'password') : type
            }
            value={value}
            onChange={handleChange}
            onBlur={onBlur}
            ref={ref}
            style={{ minWidth }}
            placeholder={getPlaceholderWithAsterisk()}
            className={cx(
              styles.mainInput,
              isInvalid ? styles.error : '',
              className,
            )}
            autoComplete={autocomplete}
            {...(type === 'number' && min !== undefined ? { min } : {})}
            {...(type === 'number' && max !== undefined ? { max } : {})}
          />
          {type === 'password' && (
            <div className={styles.passwordButton}>
              <IconButtonForPass
                icon={<Icons.NoShow />}
                ariaLabel="показать"
                onClick={toggleShowPassword}
                isActive={showPassword}
              />
            </div>
          )}
        </div>
        <div className={styles.errorContainer}>
          {isInvalid && <span className={styles.errorText}>{error}</span>}
        </div>
      </div>
    );
  },
);

ControlledInput.displayName = 'ControlledInput';

export default ControlledInput;
